import { History } from 'history';
import { useEffect, useRef } from 'react';
import { Redirect, Route, Router, Switch, useLocation, useRouteMatch } from 'react-router';
import { getDevsitePageScrollElement } from 'modules/common/components/ScrollableContext/devsitePageScroll';
import { FilterableComponent } from 'modules/common/hooks/useFiltered';
import useFilteredChildren, { FilterableChildren } from 'modules/common/hooks/useFilteredChildren';
import { Analytics } from 'modules/common/util/Analytics';
import authenticationRedirectHandler from '../../auth/authenticationRedirectHandler';
import { DevsiteRouteProps } from './DevsiteRoute';
import Error403 from './Error403';
import Error404 from './Error404';
import ErrorPageProvider, { Error404Trigger } from './ErrorPageProvider';
import { LegacyRedirectRule, LegacyRedirects } from './LegacyRedirects';

export type RoutingChildren = FilterableChildren<DevsiteRouteProps>;
interface RoutingProps {
  children: RoutingChildren;
  redirectToFirst?: boolean;
  /** Legacy URL redirects, tried in order before any of the routes. */
  redirects?: LegacyRedirectRule[];
}

const isRoute = (child: FilterableComponent<DevsiteRouteProps>) => 'path' in child.props;

/**
 * This is our mini cloud front. Best part? No restrictions on the number of routes :)
 *
 * The Routing component is a better version of the React Router Switch statement. Unlike
 * the Switch in React Router, Routing supports React Fragments (yay) so you can easily
 * split up your routes if need be.
 *
 * Routing also only works with DevsiteRoute or OrgPermissionedRoute. There is rarely a case where you would need
 * to redirect to a random route. If you need to redirect to the first route in the list if
 * none match, the "redirectToFirst" prop should work for you.
 *
 * Routing also handles 404 cases out of the box so there is no need for a catch all route
 */
export const Routing: React.FC<RoutingProps> = (props) => {
  const match = useRouteMatch();
  const children = useFilteredChildren(props.children);

  if (children.length === 0) {
    authenticationRedirectHandler(403);
    return null;
  }

  const redirect = props.redirectToFirst ? (
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    <Redirect exact from={match.path} to={children.find(isRoute).props.path as string} />
  ) : (
    <Route render={() => <Error404Trigger />} />
  );
  const routes = (
    <Switch>
      {children}
      {redirect}
      {/* below is needed because react router is annoying */}
      {props.redirectToFirst && <Route render={() => <Error404Trigger />} />}
    </Switch>
  );

  // Always wrap, so a change in the number of redirects doesn't remount the routes.
  return <LegacyRedirects rules={props.redirects ?? []}>{routes}</LegacyRedirects>;
};

const ScrollToTop: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { pathname, search } = useLocation();
  const prev = useRef({ pathname, search });

  useEffect(() => {
    // Hash-only changes are in-page anchors — do not reset scroll (would cancel the jump).
    if (pathname !== prev.current.pathname || search !== prev.current.search) {
      const pageScroll = getDevsitePageScrollElement();
      if (pageScroll) pageScroll.scrollTo(0, 0);
      else window.scrollTo(0, 0);
    }
    prev.current = { pathname, search };
  }, [pathname, search]);

  return <>{children}</>;
};

/** Tracks a page view on the initial load and on every location change. */
const PageViewTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    Analytics.trackLocationPageView(location);
  }, [location]);

  return null;
};

interface AppRouterProps {
  history: History;
}

export const AppRouter: React.FC<React.PropsWithChildren<AppRouterProps>> = ({ history, children }) => (
  <Router history={history}>
    <PageViewTracker />
    <ScrollToTop>
      <ErrorPageProvider render404={() => <Error404 />} render403={() => <Error403 />}>
        {children}
      </ErrorPageProvider>
    </ScrollToTop>
  </Router>
);
